import * as Sentry from "@sentry/react";
import React from "react";
import "moment-business-time"; // extend moment
import { configure } from "mobx";
import("typeface-roboto");
import "@bonliva-crm/common/extensions";
import { createRoot } from "react-dom/client";
import { createBrowserHistory } from "history";
import { syncHistoryWithStore } from "mobx-react-router";
import { Provider } from "mobx-react";
import { ApolloProvider } from "@apollo/client";
import { ThemeProvider } from "@material-ui/core/styles";
import { MsalProvider } from "@azure/msal-react";
import { EventMessage, EventType, AuthenticationResult } from "@azure/msal-browser";
import App from "./app/App";
import { msalInstance, initializeMsal, loginRequest } from "./app/msalConfig";
import { Store } from "./app/store/Store";
import { client } from "./app/graphql/client";
import { bonlivaTheme } from "./app/theme";
import "./app/theme/animations.css";
import { environment } from "./environments/environment";
import { SnackbarProvider } from "@bonliva-crm/ui";
import {
  installChunkLoadRecovery,
  isChunkLoadError,
  shouldReportChunkLoadError
} from "./app/common/chunk-load-recovery";

console.info(
  `Starting app ${environment.version} (build ${environment.build}): ${environment.businessEnv}`
);

const browserHistory = createBrowserHistory();

// Must be installed before Sentry.init so chunk load failures after a deploy
// trigger a guarded reload instead of surfacing as errors (CRMDEV-7093).
installChunkLoadRecovery();

Sentry.init({
  environment: environment.businessEnv,
  release: environment.version,
  dsn: environment.sentryDsn,
  integrations: [Sentry.browserApiErrorsIntegration(), Sentry.httpClientIntegration()],
  tracesSampleRate: 0,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0,
  tracePropagationTargets: [],
  beforeSend(event, hint) {
    if (event?.request?.url?.includes("/api/poll")) {
      return null;
    }
    // Chunk load failures right after a deploy are auto-recovered with a
    // guarded reload — do not report those. If the guarded reload was already
    // used (chunk genuinely broken), let the event through (CRMDEV-7093).
    const errorValue = event?.exception?.values?.[0]?.value;
    if (
      (isChunkLoadError(hint?.originalException) || isChunkLoadError(errorValue)) &&
      !shouldReportChunkLoadError()
    ) {
      return null;
    }
    return event;
  }
});

configure({ enforceActions: "never" });

const container = document.getElementById("root");
const root = createRoot(container);

const bootstrap = async () => {
  try {
    await initializeMsal();

    const tokenResponse = await msalInstance.handleRedirectPromise();
    const accounts = msalInstance.getAllAccounts();

    if (tokenResponse && tokenResponse.account) {
      msalInstance.setActiveAccount(tokenResponse.account);
    } else if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    } else {
      msalInstance
        .loginRedirect({
          scopes: loginRequest.scopes
        })
        .catch((error) => {
          if (error?.errorCode !== "interaction_in_progress") {
            console.error("Login redirect error:", error);
          }
        });

      return;
    }

    const existingStore = window.store;
    const storeInstance = existingStore || new Store();
    window.store = storeInstance;

    if (environment.businessEnv !== "production") {
      window["Store"] = storeInstance;
      // eslint-disable-next-line
      (window as any).toJS = require("mobx").toJS;
    }

    const history = syncHistoryWithStore(browserHistory, storeInstance.routing);

    msalInstance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        const account = payload.account;
        msalInstance.setActiveAccount(account);
      }
    });

    root.render(
      <MsalProvider instance={msalInstance}>
        <Provider store={storeInstance} rootStore={storeInstance}>
          <ApolloProvider client={client}>
            <ThemeProvider theme={bonlivaTheme}>
              <SnackbarProvider>
                <App history={history} />
              </SnackbarProvider>
            </ThemeProvider>
          </ApolloProvider>
        </Provider>
      </MsalProvider>
    );
  } catch (error) {
    console.error("Error initializing MSAL:", error);
  }
};

bootstrap();
